import type { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';

/**
 * Input transform for the CMS-provided `baseUrl`: accepts only a valid
 * https URL and normalizes it to origin + path without trailing slashes.
 * Anything else is rejected and results in an empty string.
 */
export function toBaseUrl(value: string): string {
	try {
		const url = new URL(value);
		if (url.protocol !== 'https:') return '';

		let base = `${url.origin}${url.pathname}`;
		while (base.endsWith('/')) {
			base = base.slice(0, -1);
		}
		return base;
	} catch {
		return '';
	}
}

/**
 * Build an URL for a list item: validated `baseUrl` + slug. Undefined
 * when no `baseUrl` is configured.
 */
export function toPageUrl(baseUrl: string, slug: string): string | undefined {
	return baseUrl ? `${baseUrl}/${slug}` : undefined;
}

export function sortByOption<T extends { name: string; price: PriceInterface }>(
	items: T[],
	option: string,
): T[] {
	const sorted = [...items];

	switch (option) {
		case 'name-asc':
			return sorted.sort((a, b) => a.name.localeCompare(b.name));
		case 'name-desc':
			return sorted.sort((a, b) => b.name.localeCompare(a.name));
		case 'price-asc':
			return sorted.sort((a, b) => {
				if (a.price.amount === null && b.price.amount === null)
					return 0;
				if (a.price.amount === null) return 1;
				if (b.price.amount === null) return -1;
				return a.price.amount - b.price.amount;
			});
		case 'price-desc':
			return sorted.sort((a, b) => {
				if (a.price.amount === null && b.price.amount === null)
					return 0;
				if (a.price.amount === null) return 1;
				if (b.price.amount === null) return -1;
				return b.price.amount - a.price.amount;
			});
		default:
			return sorted;
	}
}
